import { useContext, useEffect, useRef, useCallback } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

const DEFAULT_MESSAGE =
  "You have unsaved changes. Are you sure you want to leave?";

/**
 * Warns the user before they leave a page with unsaved changes (BBD-56).
 *
 * Covers all three ways of leaving:
 *  1. Browser-level exits — closing the tab/window, refreshing, or typing an
 *     external URL (native `beforeunload` dialog).
 *  2. In-app navigation — every <Link> click and programmatic `navigate()`
 *     call funnels through the router's navigator, whose push/replace are
 *     intercepted with a confirm dialog while changes are unsaved.
 *  3. The browser Back/Forward buttons — a sentinel history entry is armed so
 *     the first Back stays on the page and asks for confirmation.
 *
 * Usage:
 *   const { releaseGuard } = useUnsavedChangesGuard(hasUnsavedChanges);
 *   ...
 *   setHasUnsavedChanges(false);
 *   releaseGuard(); // synchronous — call right before navigate() after a save
 *   navigate("/somewhere");
 *
 * `releaseGuard()` exists because React state updates are async: calling
 * `setHasUnsavedChanges(false)` immediately followed by `navigate()` would
 * still hit the interceptor. Releasing flips a ref checked synchronously.
 * The guard re-arms automatically the next time `when` becomes true again.
 */
function useUnsavedChangesGuard(when, message = DEFAULT_MESSAGE) {
  const { navigator } = useContext(UNSAFE_NavigationContext);

  // Checked synchronously by every handler; releaseGuard() can clear it
  // mid-tick, and any change to `when` re-syncs it on the next render.
  const activeRef = useRef(when);
  const prevWhenRef = useRef(when);
  if (prevWhenRef.current !== when) {
    prevWhenRef.current = when;
    activeRef.current = when;
  }

  const releaseGuard = useCallback(() => {
    activeRef.current = false;
  }, []);

  // 1. Browser-level exits: tab close, refresh, external URL.
  useEffect(() => {
    if (!when) return;
    const handleBeforeUnload = (event) => {
      if (!activeRef.current) return;
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [when, message]);

  // 2. In-app navigation: intercept the router navigator's push/replace so
  //    both <Link> clicks and navigate() calls confirm first.
  useEffect(() => {
    if (!when) return;
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    navigator.push = (...args) => {
      if (!activeRef.current || window.confirm(message)) {
        activeRef.current = false;
        originalPush.call(navigator, ...args);
      }
    };
    navigator.replace = (...args) => {
      if (!activeRef.current || window.confirm(message)) {
        activeRef.current = false;
        originalReplace.call(navigator, ...args);
      }
    };

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [navigator, when, message]);

  // 3. Back/Forward buttons: arm a sentinel history entry once, so pressing
  //    Back first pops the sentinel (URL unchanged — the router ignores it),
  //    giving us a chance to confirm before really going back.
  const sentinelArmedRef = useRef(false);
  useEffect(() => {
    if (!when) return;

    if (!sentinelArmedRef.current) {
      window.history.pushState(null, "", window.location.href);
      sentinelArmedRef.current = true;
    }

    // While a confirmed back-navigation is in flight, let popstate pass
    // through untouched so the router can complete it.
    let navigationConfirmed = false;

    const handlePopState = () => {
      if (navigationConfirmed) return;
      if (!activeRef.current) return;
      if (window.confirm(message)) {
        navigationConfirmed = true;
        activeRef.current = false;
        window.history.back();
      } else {
        // Re-arm the sentinel so the next Back press is caught again.
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [when, message]);

  return { releaseGuard };
}

export default useUnsavedChangesGuard;
